import React, { useState, useContext, useEffect, useCallback } from 'react';
import ModalContext from '../../../../contexts/ModalContext';
import { useOutletContext } from 'react-router-dom';
import { ProductionFashionOutletType } from '..';
import { GlobalTable, HeaderLine, Td, Th, Tr, FooterTr } from '../../../../components/styles/table-style';
import { Actions, Select } from './style';
import arrowDown from '../../../../assets/addStockModal/excel/arrow-down.svg';
import search from '../../../../assets/productions/search-in.svg';
import pen from '../../../../assets/productions/pen.svg';
import play from '../../../../assets/production-flow/play-circle.svg';
import FashionApiService from '../../../../services/FashionApiService';
import eventBus from '../../../../utils/eventBus';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import { LoadMoreProvider, useLoadMore } from '../../../../contexts/LoadMoreContext';


export interface CompanyData {
  id: number;
  companyName: string;
}

interface FashionProductionItemsData {
  id: number;
  fashionProductionOrderId: number;
  fashionOrderId: number;
  quantityForThisProduction: number;
  fashionOrder?: {
    id: number;
  };
}

interface UserProfileData {
  id: number;
  name: string;
}

interface FashionProductionOrderData {
  id: number;
  set: string;
  status: string;
  dressmakerId: number | null;
  cutterId: number | null;
  fashionProductId?: number;
  colorId?: number;

  fashionProduct?: { id: number; name: string };
  color?: { id: number; name: string };
  fashionProductionItems?: FashionProductionItemsData[];

  dressmaker?: UserData;
  cutter?: UserData;

  totalItems?: number;
}

interface UserData {
  id: number;
  name: string; 
  email?: string;
  profile?: UserProfileData;
  profileId?: number | null;
}


const ProductionFashionTableContent: React.FC = () => {
  const { setTabIndex } = useOutletContext<ProductionFashionOutletType>();
  const { setModalActive, setModalSection, setSelectedProductionOrder } = useContext(ModalContext);
  const { hasNextPage, isFetchingNextPage, setIsFetchingNextPage, setFetchNextPageFunction, fetchNextPage, setHasNextPage } = useLoadMore();

  const columns = ["OP", "Pedido", "Conjunto", "Cor", "Qtd. de itens", "Status", "Cortador", "Costureira", ""];

  const [productionOrders, setProductionOrders] = useState<FashionProductionOrderData[]>([]);
  const [cuttersList, setCuttersList] = useState<UserData[]>([]);
  const [dressmakersList, setDressmakersList] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const fetchData = useCallback(async (currentPage: number = 0, append: boolean = false) => {
    try {
      setIsLoading(currentPage === 0);
      if (currentPage > 0) setIsFetchingNextPage(true);
      setError(null);

      console.log('🔍 [FETCH DATA] currentPage:', currentPage, 'append:', append);

      const totalResponse = await FashionApiService.getProductionOrdersCount();
      const total = totalResponse.data?.total || 0;
      setTotalCount(total);

      const productionOrdersResponse = await FashionApiService.getProductionOrders(currentPage, pageSize);

      const [cuttersResp, dressmakersResp] = await Promise.all([
        FashionApiService.getUsers('Cortador'),
        FashionApiService.getUsers('Costureira')
      ]);

      const cutters: UserData[] = cuttersResp.data || [];
      const dressmakers: UserData[] = dressmakersResp.data || [];

      if (cutters.length === 0) console.warn("Nenhum cortador retornado pelo backend para este tenant.");
      if (dressmakers.length === 0) console.warn("Nenhuma costureira retornada pelo backend para este tenant.");

      if (currentPage === 0) {
        setCuttersList([{ id: 0, name: "Selecione..." }, ...cutters]);
        setDressmakersList([{ id: 0, name: "Selecione..." }, ...dressmakers]);
      }

      const mappedOrders: FashionProductionOrderData[] = productionOrdersResponse.data.map((order: FashionProductionOrderData) => {
        const totalItemsInProduction = order.fashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

        return {
          ...order,
          totalItems: totalItemsInProduction,
        };
      });

      const returnedItemsCount = mappedOrders.length;
      const hasMoreData = returnedItemsCount === pageSize;
      
      if (append) {
        setProductionOrders(prev => {
          const existingIds = new Set(prev.map(o => o.id));
          const newOrders = mappedOrders.filter(o => !existingIds.has(o.id));
          const updatedOrders = [...prev, ...newOrders];
          const hasMore = updatedOrders.length < total && hasMoreData;
          setHasNextPage(hasMore);
          console.log('➕ [PRODUCTION] Append - hasMore:', hasMore, 'prev.length:', prev.length, 'newOrders.length:', newOrders.length, 'updatedOrders.length:', updatedOrders.length, 'total:', total, 'hasMoreData:', hasMoreData);
          return updatedOrders;
        });
      } else {
        setProductionOrders(mappedOrders);
        const hasMore = mappedOrders.length < total && hasMoreData;
        setHasNextPage(hasMore);
        console.log('🆕 [PRODUCTION] First load - hasMore:', hasMore, 'mappedOrders.length:', mappedOrders.length, 'total:', total, 'hasMoreData:', hasMoreData);
      }

    } catch (err: any) {
      console.error('Erro ao buscar dados:', err);
      setError('Falha ao carregar ordens de produção ou usuários.');
    } finally {
      setIsLoading(false);
      setIsFetchingNextPage(false);
    }
  }, [pageSize, setIsFetchingNextPage, setHasNextPage]);

  useEffect(() => {
    const handleRefreshTable = () => {
      setPage(0);
      fetchData(0, false);
    };

    eventBus.on('refreshProductionTable', handleRefreshTable);

    return () => {
      eventBus.off('refreshProductionTable', handleRefreshTable);
    };
  }, [fetchData]);

  useEffect(() => {
    fetchData(0, false);
  }, []);

  useEffect(() => {
    if (page > 0) {
      console.log('📄 [PAGE CHANGED] Fetching page:', page);
      fetchData(page, true);
    }
  }, [page, fetchData]);

  useEffect(() => {
    const loadMoreFunction = () => {
      if (!isFetchingNextPage) {
        console.log('🔼 [LOAD MORE] Current page:', page, 'Setting to:', page + 1);
        setPage((prevPage) => prevPage + 1);
      }
    };
    setFetchNextPageFunction(loadMoreFunction);
  }, [setFetchNextPageFunction, page, isFetchingNextPage]);


  useEffect(() => {
    if (productionOrders.length > 0 && cuttersList.length > 1 && dressmakersList.length > 1) { // >1 para ignorar "Selecione..."
      productionOrders.forEach(orderItem => {
        const assignedCutter = cuttersList.find(c => c.id === orderItem.cutterId);
        const assignedDressmaker = dressmakersList.find(d => d.id === orderItem.dressmakerId);
      });
    }
  }, [productionOrders, cuttersList, dressmakersList]);

  const handleCortadorChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = cuttersList.find(c => c.id === selectedUserId);

    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          cutterId: selectedUserId === 0 ? null : selectedUserId,
          cutter: selectedUserId === 0 ? undefined : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        cutterId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newCutterName = selectedUserObject?.name || 'Nenhum';
      alert(`SUCESSO: Cortador da OP-${orderId} atualizado para ${newCutterName}!`);
    } catch (err) {
      console.error(`Erro ao atualizar cortador da OP ${orderId}:`, err);
      alert(`ERRO: Falha ao atualizar cortador da OP-${orderId}. Verifique o console.`);
      fetchData();
    }
  };

  const handleCostureiraChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = dressmakersList.find(d => d.id === selectedUserId);

    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          dressmakerId: selectedUserId === 0 ? null : selectedUserId,
          dressmaker: selectedUserId === 0 ? undefined : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        dressmakerId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newDressmakerName = selectedUserObject?.name || 'Nenhum';
      alert(`SUCESSO: Costureira da OP-${orderId} atualizada para ${newDressmakerName}!`);
    } catch (err) {
      console.error(`Erro ao atualizar costureira da OP ${orderId}:`, err);
      alert(`ERRO: Falha ao atualizar costureira da OP-${orderId}. Verifique o console.`);
      fetchData();
    }
  };

  const handleView = (order: FashionProductionOrderData) => {
    setSelectedProductionOrder(order);
    setModalActive(true);
    setModalSection('viewProductionFashion');
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Pendente',
      'iniciado': 'Iniciado',
      'in_progress': 'Em Progresso',
      'completed': 'Concluído',
      'cancelled': 'Cancelado'
    };
    return statusMap[status] || status;
  };

  const handleStartProduction = async (orderId: number) => {
    try {
      await FashionApiService.updateProductionOrder(orderId, {
        status: 'iniciado',
      });

      setProductionOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: 'iniciado' } : order
        )
      );

      alert(`Produção da OP-${orderId} iniciada com sucesso!`);
    } catch (err) {
      console.error(`Erro ao iniciar produção da OP ${orderId}:`, err);
      alert(`ERRO: Falha ao iniciar produção da OP-${orderId}. Verifique o console.`);
      fetchData(0, false);
    }
  };

  if (isLoading) {
    return <div>Carregando ordens de produção...</div>;
  }

  if (error) {
    return <div>Erro: {error}</div>;
  }

  return (
    <GlobalTable>
      <thead>
        <HeaderLine>
          {columns.map((item, index) => (
            <Th key={index}>{item}</Th>
          ))}
        </HeaderLine>
      </thead>
      <tbody>
        {productionOrders.map((orderItem) => (
          <Tr key={orderItem.id}>
            <Td>{`OP-${orderItem.id}`}</Td>
            <Td>
              {orderItem.fashionProductionItems && orderItem.fashionProductionItems.length > 0
                ? orderItem.fashionProductionItems.map(item => `PED-${item.fashionOrderId}`).join(', ')
                : 'N/A'}
            </Td>
            <Td>{orderItem.set}</Td>
            <Td>{orderItem.color?.name || 'N/A'}</Td>
            <Td>{orderItem.totalItems}</Td>
            <Td>{getStatusLabel(orderItem.status)}</Td>
            <Td>
              <div>
                <Select
                  value={orderItem.cutterId || 0}
                  onChange={(e) => handleCortadorChange(e, orderItem.id)}
                  className="p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {cuttersList.map((cutter) => (
                    <option key={cutter.id} value={cutter.id}>
                      {cutter.name}
                    </option>
                  ))}
                </Select>
                <img style={{ marginLeft: '-2rem', pointerEvents: 'none' }} src={arrowDown} alt="Seta para baixo" />
              </div>
            </Td>
            <Td>
              <div>
                <Select
                  value={orderItem.dressmakerId || 0}
                  onChange={(e) => handleCostureiraChange(e, orderItem.id)}
                  className="p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {dressmakersList.map((dressmaker) => (
                    <option key={dressmaker.id} value={dressmaker.id}>
                      {dressmaker.name}
                    </option>
                  ))}
                </Select>
                <img style={{ marginLeft: '-2rem', pointerEvents: 'none' }} src={arrowDown} alt="Seta para baixo" />
              </div>
            </Td>
            <Td>
              <Actions>
                <img onClick={() => handleView(orderItem)} src={search} alt="Visualizar ordem" style={{ cursor: 'pointer' }} />
                <img src={pen} alt="Editar ordem" style={{ cursor: 'pointer' }} />
                <img onClick={() => handleStartProduction(orderItem.id)} src={play} alt="Iniciar ordem" style={{ cursor: 'pointer' }} />
              </Actions>
            </Td>
          </Tr>
        ))}
      </tbody>
      <tfoot>
        <FooterTr>
          <Td colSpan={9} style={{ textAlign: 'center', background: 'transparent', padding: '1rem 2rem' }}>
            <LoadMoreButton
              currentCount={productionOrders.length}
              totalCount={totalCount}
              onLoadMore={fetchNextPage}
              isLoading={isFetchingNextPage}
              hasMore={hasNextPage}
            />
          </Td>
        </FooterTr>
      </tfoot>
    </GlobalTable>
  );
};

const ProductionFashionTable: React.FC = () => (
  <LoadMoreProvider>
    <ProductionFashionTableContent />
  </LoadMoreProvider>
);

export default ProductionFashionTable;